import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import { BarChart3, FileText, Calendar, ArrowDownRight, TrendingUp, Clock, Sun } from 'lucide-react';
import Skeleton from './Skeleton';

interface DayAverage {
  dayName: string;
  dayShortName: string;
  dayNumber: number;
  totalRevenue: number;
  count: number;
  average: number;
  maxRevenue: number;
  dailyRevenues: number[];
}

export default function DayOverview() {
  const [loading, setLoading] = useState(true);
  const [dayAverages, setDayAverages] = useState<DayAverage[]>([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [activeYears, setActiveYears] = useState<number[]>([]);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [showTips, setShowTips] = useState(false);

  useEffect(() => {
    fetchActiveYears();
  }, []);

  useEffect(() => {
    if (activeYears.length > 0) {
      fetchDayAverages();
    }
  }, [currentYear, activeYears, showTips]);

  const fetchActiveYears = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: settings } = await supabase
        .from('settings')
        .select('active_years, show_tips')
        .eq('user_id', user.id)
        .maybeSingle();

      if (settings) {
        if (settings.active_years) {
          const years = settings.active_years.sort((a, b) => a - b);
          setActiveYears(years);
        }
        setShowTips(settings.show_tips ?? true);
      }
    } catch (error) {
      console.error('Error fetching active years:', error);
    }
  };

  const fetchDayAverages = async () => {
    setLoading(true);

    try {
      // Use the correct filter syntax for date comparison
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;

      const { data: entries, error } = await supabase
        .from('revenue_entries')
        .select('*')
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true });

      if (error) throw error;

      // Fetch tips (hidden revenue) for the year if show_tips is enabled
      let tipsMap: Record<string, number> = {};
      if (showTips) {
        const { data: tipsData } = await supabase
          .from('hidden_revenue')
          .select('date, amount')
          .gte('date', startDate)
          .lte('date', endDate);

        if (tipsData) {
          // Create a map of date -> total tips for that date
          tipsData.forEach(tip => {
            if (!tipsMap[tip.date]) {
              tipsMap[tip.date] = 0;
            }
            tipsMap[tip.date] += tip.amount;
          });
        }
      }

      // Initialize averages for each day
      const dayTotals = Array.from({ length: 7 }, (_, i) => ({
        dayName: format(new Date(2024, 0, i + 1), 'EEEE', { locale: nl }),
        dayShortName: format(new Date(2024, 0, i + 1), 'EEEEE', { locale: nl }),
        dayNumber: i,
        totalRevenue: 0,
        count: 0,
        average: 0,
        maxRevenue: 0,
        dailyRevenues: [] as number[],
      }));

      // Calculate totals and track daily revenues
      entries?.forEach(entry => {
        const date = parseISO(entry.date);
        const dayOfWeek = date.getDay();
        const dayIndex = (dayOfWeek + 6) % 7; // Adjust to make Monday = 0
        const revenueTotal = entry.cash_revenue + entry.card_revenue + entry.invoice_revenue;
        const tipsAmount = showTips ? (tipsMap[entry.date] || 0) : 0;
        const dailyTotal = revenueTotal + tipsAmount;

        if (dailyTotal > 0) {
          dayTotals[dayIndex].totalRevenue += dailyTotal;
          dayTotals[dayIndex].count += 1;
          dayTotals[dayIndex].dailyRevenues.push(dailyTotal);
          dayTotals[dayIndex].maxRevenue = Math.max(dayTotals[dayIndex].maxRevenue, dailyTotal);
        }
      });

      // Calculate averages
      const averagesWithData = dayTotals.map(day => ({
        ...day,
        average: day.count > 0 ? day.totalRevenue / day.count : 0,
      }));

      setDayAverages(averagesWithData);
    } catch (error) {
      console.error('Error fetching day averages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (year: number) => {
    setCurrentYear(year);
    setYearDropdownOpen(false);
  };

  // Find the maximum average across all days for scaling
  const maxAverage = Math.max(...dayAverages.map(day => day.average));

  // Calculate totals for all days
  const totalStats = dayAverages.reduce(
    (acc, day) => {
      acc.totalRevenue += day.totalRevenue;
      acc.totalCount += day.count;
      return acc;
    },
    { totalRevenue: 0, totalCount: 0 }
  );

  // Calculate overall average
  const overallAverage = totalStats.totalCount > 0 
    ? totalStats.totalRevenue / totalStats.totalCount 
    : 0;

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-8 w-32" />
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <Skeleton className="h-5 w-32 mb-2" />
              <Skeleton className="h-8 w-24 mb-1" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>

        {/* Day Circles Card Skeleton */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="space-y-6">
            <div className="flex items-center space-x-2">
              <Skeleton className="h-5 w-32" />
            </div>
            
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-4 justify-items-center">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="flex flex-col items-center space-y-2">
                  <Skeleton className="h-16 w-16 rounded-full" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-3 w-12 hidden sm:block" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Details Card Skeleton */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Skeleton className="h-5 w-32" />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      {[...Array(4)].map((_, i) => (
                        <th key={i} className="px-4 py-3">
                          <Skeleton className="h-4 w-24" />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(7)].map((_, rowIndex) => (
                      <tr key={rowIndex}>
                        {[...Array(4)].map((_, colIndex) => (
                          <td key={colIndex} className="px-4 py-3">
                            <Skeleton className="h-6 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Year Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dagoverzicht {currentYear}</h1>
        
        <div className="relative">
          <button
            onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
            className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <span>Jaar: {currentYear}</span>
            <ArrowDownRight className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${yearDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {yearDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
              <div className="py-1">
                {activeYears.map(year => (
                  <button
                    key={year}
                    onClick={() => handleYearChange(year)}
                    className={`block w-full text-left px-4 py-2 text-sm ${
                      year === currentYear
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <Sun className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Gemiddelde Omzet
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{overallAverage.toFixed(2)}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Per werkdag
          </p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Totale Omzet
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            €{totalStats.totalRevenue.toFixed(2)}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Alle dagen
          </p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Werkdagen
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            {totalStats.totalCount}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Geregistreerde dagen
          </p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Beste Dag
            </h2>
          </div>
          <p className="mt-2 text-xl font-bold text-gray-900 dark:text-white">
            {dayAverages.reduce((best, day) => day.average > best.average ? day : best, dayAverages[0]).dayName}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Hoogste gemiddelde
          </p>
        </div>
      </div>

      {/* Day Circles Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Sun className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Gemiddelde Omzet per Dag
          </h2>
        </div>
        
        {/* Day Progress Circles - Responsive Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-4 justify-items-center">
          {dayAverages.map((day) => {
            const percentage = maxAverage > 0 ? (day.average / maxAverage) * 100 : 0;
            const circumference = 2 * Math.PI * 28; // radius = 28
            const strokeDasharray = circumference;
            const strokeDashoffset = circumference - (percentage / 100) * circumference;

            return (
              <div key={day.dayNumber} className="flex flex-col items-center">
                <div className="relative w-16 h-16 sm:w-20 sm:h-20">
                  {/* Background circle */}
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="50%"
                      cy="50%"
                      r="28"
                      strokeWidth="8"
                      stroke="currentColor"
                      fill="transparent"
                      className="text-gray-200 dark:text-gray-700"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="50%"
                      cy="50%"
                      r="28"
                      strokeWidth="8"
                      stroke="currentColor"
                      fill="transparent"
                      strokeLinecap="round"
                      className="text-blue-500"
                      style={{
                        strokeDasharray: strokeDasharray,
                        strokeDashoffset: strokeDashoffset,
                        transition: 'stroke-dashoffset 1s ease-in-out',
                      }}
                    />
                  </svg>
                  {/* Day letter in center */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-base sm:text-lg font-bold text-gray-700 dark:text-gray-300">
                      {day.dayShortName}
                    </span>
                  </div>
                </div>
                <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">
                  €{day.average.toFixed(2)}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                  {day.dayName}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Details Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Dag
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Gemiddelde
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Werkdagen
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Totaal
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {dayAverages.map((day) => (
                <tr key={day.dayNumber} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Sun className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {day.dayName}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                    €{day.average.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                    {day.count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                    €{day.totalRevenue.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-blue-50/50 dark:bg-blue-900/10">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-800 dark:text-blue-200">
                  Totalen
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{(dayAverages.reduce((sum, day) => sum + day.average, 0) / 7).toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  {dayAverages.reduce((sum, day) => sum + day.count, 0)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{dayAverages.reduce((sum, day) => sum + day.totalRevenue, 0).toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}